import { useState } from 'react';
import { getAllGames } from '../config/games';
import { selectGame, clearGameSelection } from '../services/gameSelectionService';
import '../styles/GameSelection.css';

// Helper function to safely get a string value (prevents rendering objects)
const safeString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

const GameSelection = ({ roomCode, user, roomData }) => {
  const [loading, setLoading] = useState(false);
  const games = getAllGames();

  const isHost = roomData?.host?.userId === user.uid;
  const mySelection = isHost ? roomData?.host?.gameSelection : roomData?.guest?.gameSelection;
  const opponentSelection = isHost ? roomData?.guest?.gameSelection : roomData?.host?.gameSelection;
  const opponentName = safeString(isHost ? roomData?.guest?.displayName : roomData?.host?.displayName, 'Opponent');

  const handleSelectGame = async (gameId) => {
    if (loading) return;

    // Don't allow deselection if both players have already agreed
    const gameAgreed = roomData.gameSelected;
    if (mySelection === gameId && gameAgreed) {
      // Game is already agreed upon, do nothing
      return;
    }

    setLoading(true);
    try {
      if (mySelection === gameId) {
        // Deselect if clicking the same game (only if not agreed yet)
        await clearGameSelection(roomCode, user.uid);
      } else {
        await selectGame(roomCode, user.uid, gameId);
      }
    } catch (error) {
      console.error('Error selecting game:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="game-selection">
      <div className="selection-header">
        <h3>🎮 Choose a Game</h3>
        <p className="selection-subtitle">
          {mySelection && opponentSelection && mySelection === opponentSelection
            ? '✓ You both selected the same game!'
            : mySelection && opponentSelection && mySelection !== opponentSelection
            ? `You selected different games. Choose the same one to continue.`
            : mySelection
            ? 'Waiting for opponent to choose...'
            : 'Select a game to play'}
        </p>
      </div>

      <div className="games-grid">
        {games.map((game) => {
          const isMySelection = mySelection === game.id;
          const isOpponentSelection = opponentSelection === game.id;
          const isBothSelection = isMySelection && isOpponentSelection;
          const isDisabled = !game.implemented;

          return (
            <button
              key={game.id}
              className={`game-card ${isMySelection ? 'my-selection' : ''} ${
                isOpponentSelection ? 'opponent-selection' : ''
              } ${isBothSelection ? 'agreed' : ''} ${isDisabled ? 'disabled-game' : ''}`}
              onClick={() => !isDisabled && handleSelectGame(game.id)}
              disabled={loading || isDisabled}
              style={{
                borderColor: isBothSelection
                  ? '#4CAF50'
                  : isMySelection
                  ? '#667eea'
                  : isOpponentSelection
                  ? '#ff6b6b'
                  : '#ddd',
              }}
            >
              <div className="game-card-header">
                <span className="game-icon">{game.icon}</span>
                <div className="game-badges">
                  {isDisabled && (
                    <span className="selection-badge coming-soon-badge">Coming Soon</span>
                  )}
                  {!isDisabled && isMySelection && (
                    <span className="selection-badge my-badge">You</span>
                  )}
                  {!isDisabled && isOpponentSelection && (
                    <span className="selection-badge opponent-badge">
                      {opponentName.split(' ')[0] || 'Them'}
                    </span>
                  )}
                  {!isDisabled && isBothSelection && (
                    <span className="selection-badge agreed-badge">✓ Agreed!</span>
                  )}
                </div>
              </div>

              <div className="game-info">
                <h4 className="game-name">{game.displayName}</h4>
                <p className="game-description">{game.description}</p>

                <div className="game-meta">
                  <span className="meta-item">
                    {'⭐'.repeat(game.complexity)}
                  </span>
                  <span className="meta-item">⏱️ {game.duration}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {mySelection && opponentSelection && mySelection === opponentSelection && (
        <div className="agreement-message">
          <div className="success-icon">🎉</div>
          <h4>Game Selected!</h4>
          <p>
            Both players have chosen{' '}
            <strong>{games.find((g) => g.id === mySelection)?.displayName}</strong>
          </p>
          <p className="next-step">
            Either player can start the game now!
          </p>
        </div>
      )}
    </div>
  );
};

export default GameSelection;
